import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { graphql, compose, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import {
	Pane,
	SearchBar,
	ButtonGroup,
	Button,
	Icon,
	Pagination,
	Tooltip,
	Spinner,
} from '../../components';

import '../../../scss/containers/customers/_link-pane.scss';

const DEFAULT_STATE = {
	filterType: 'all',
	corporations: null,
	corporationsSearchValue: '',
	corporationsPage: 1,
	builders: null,
	buildersSearchValue: '',
	buildersPage: 1,
	selectedCorporation: null,
};

const PAGINATION_LIMIT = 10;

class LinkCustomerPane extends Pane {
	state = DEFAULT_STATE;

	constructor(props) {
		super(props);

		this.handleChangeFilterType.bind(this);
		this.handleSearchCorporations.bind(this);
		this.handleSearchBuilders.bind(this);
	}

	componentWillReceiveProps(nextProps) {
		const didCustomerChange = nextProps.customer && this.props.customer !== nextProps.customer;

		if (didCustomerChange) {
			this.fetchData();
		}
	}

	close() {
		super.close();

		// Reset the state when the panel is close
		this.setState(DEFAULT_STATE);
	}

	handleSearchCorporations(corporationsSearchValue) {
		this.setState(
			{
				corporationsSearchValue,
				selectedCorporation: null,
				corporationsPage: 1,
			},
			this.fetchData,
		);
	}

	handleSearchBuilders(buildersSearchValue) {
		this.setState({ buildersSearchValue, buildersPage: 1 }, this.fetchBuildersList);
	}

	handleChangeFilterType(filterType) {
		this.setState(
			{ filterType, selectedCorporation: null, corporationsPage: 1 },
			this.fetchData,
		);
	}

	handleClickCorporation(e, selectedCorporation) {
		if (!e.isDefaultPrevented()) {
			e.preventDefault();

			this.setState(
				{
					selectedCorporation,
					buildersSearchValue: '',
				},
				this.fetchBuildersList,
			);
		}
	}

	async handleClickLinkCorporation(e, corporation) {
		e.preventDefault();

		const message = corporation.customer
			? `The corporation "${corporation.name}" is already linked to the customer "${corporation.customer.name}". Are you sure you want to link this corporation to the customer "${this.props.customer.name}" instead?`
			: `Are you sure you want to link the corporation "${corporation.name}" to the customer "${this.props.customer.name}"?`;

		if (confirm(message)) {
			await this.props.updateCorporation(corporation.id, this.props.customer.id);
			this.fetchData();
		}
	}

	async handleClickUnlinkCorporation(e, corporation) {
		e.preventDefault();

		if (
			confirm(
				`Are you sure you want to unlink the corporation "${corporation.name}" from the customer "${this.props.customer.name}"?`,
			)
		) {
			await this.props.updateCorporation(corporation.id, null);

			// Clear the selected corporation if we are unlinking the currently-selected corporation
			// and are filtering to show connected corporations only
			if (
				this.state.filterType === 'connected' &&
				corporation.id === this.state.selectedCorporation.id
			) {
				this.setState({ selectedCorporation: null });
			}

			this.fetchData();
		}
	}

	async handleClickLinkBuilder(e, builder) {
		e.preventDefault();

		const message = builder.customer
			? `The division "${builder.name}" is already linked to the customer "${builder.customer.name}". Are you sure you want to link this division to the customer "${this.props.customer.name}" instead?`
			: `Are you sure you want to link the division "${builder.name}" to the customer "${this.props.customer.name}"?`;

		if (confirm(message)) {
			await this.props.updateBuilder(builder.id, this.props.customer.id);
			this.fetchData();
		}
	}

	async handleClickUnlinkBuilder(e, builder) {
		e.preventDefault();

		if (
			confirm(
				`Are you sure you want to unlink the division "${builder.name}" to the customer "${this.props.customer.name}"?`,
			)
		) {
			await this.props.updateBuilder(builder.id, null);
			this.fetchData();
		}
	}

	fetchData() {
		// Fetch the corporations list
		this.fetchCorporationsList();

		// If a corporation is currently selected then
		// fetch the builders for that corporation.
		if (this.state.selectedCorporation) {
			this.fetchBuildersList();
		}
	}

	async fetchCorporationsList() {
		this.setState({
			isFetchingCorporations: true,
			builders: null,
		});

		try {
			const limit = PAGINATION_LIMIT;
			const skip = this.state.corporationsPage * limit - limit;
			const filter = {};

			if (this.state.corporationsSearchValue) {
				filter.query = `%${this.state.corporationsSearchValue}%`;
			}
			if (this.state.filterType === 'connected') {
				filter.customer = {
					link: 'ANY',
					id: this.props.customer.id,
				};
			}

			const {
				data: { corporations },
			} = await this.props.client.query({
				query: gql`
					query CorporationsQuery(
						$filter: CorporationFilters!
						$limit: Int!
						$skip: Int!
					) {
						corporations(filter: $filter, limit: $limit, skip: $skip) {
							numTotal
							results {
								id
								name
								number
								customerId
								customer {
									name
								}
							}
						}
					}
				`,
				fetchPolicy: 'network-only',
				variables: {
					limit,
					skip,
					filter,
				},
			});

			this.setState({
				isFetchingCorporations: false,
				corporations,
			});
		} catch (err) {
			this.setState({
				isFetchingCorporations: false,
				corporations: null,
			});
		}
	}

	async fetchBuildersList() {
		this.setState({
			isFetchingBuilders: true,
		});

		try {
			const limit = PAGINATION_LIMIT;
			const skip = this.state.buildersPage * limit - limit;
			const filter = {
				corporationId: { eq: this.state.selectedCorporation.id },
			};

			if (this.state.buildersSearchValue) {
				filter.query = `%${this.state.buildersSearchValue}%`;
			}
			if (this.state.filterType === 'connected') {
				filter.customer = {
					link: 'ANY',
					id: this.props.customer.id,
				};
			}

			const {
				data: { builders },
			} = await this.props.client.query({
				query: gql`
					query BuildersQuery($filter: BuilderFilters!, $limit: Int!, $skip: Int!) {
						builders(filter: $filter, limit: $limit, skip: $skip) {
							numTotal
							results {
								id
								name
								number
								customerId
								customer {
									name
								}
							}
						}
					}
				`,
				fetchPolicy: 'network-only',
				variables: {
					limit,
					skip,
					filter,
				},
			});

			this.setState({
				isFetchingBuilders: false,
				builders,
			});
		} catch (err) {
			this.setState({
				isFetchingBuilders: false,
				builders: null,
			});
		}
	}

	renderContent() {
		const { isFetchingCorporations, isFetchingBuilders, corporations, builders } = this.state;
		const corporationResults = _.get(corporations, 'results') || [];
		const numTotalCorporations = _.get(corporations, 'numTotal') || 0;
		const builderResults = _.get(builders, 'results') || [];
		const numTotalBuilders = _.get(builders, 'numTotal') || 0;

		return (
			<div className="row row-halfs customer-link-pane">
				<div className="cell left-column">
					<div className="column-header">
						<ButtonGroup
							selected={this.state.filterType}
							size="small"
							onChange={filterType => this.handleChangeFilterType(filterType)}>
							<Button name="all">All</Button>
							<Button name="connected">Connected</Button>
						</ButtonGroup>
						<SearchBar
							initialValue={this.state.corporationsSearchValue}
							onEnter={value => this.handleSearchCorporations(value)}
						/>
					</div>

					<Choose>
						<When condition={isFetchingCorporations}>
							<p>
								<Spinner visible={isFetchingCorporations} />
							</p>
						</When>
						<When condition={corporationResults.length}>
							{corporationResults.map(corporation => {
								const classes = ['corporation-card'];
								const customerName = corporation.customer
									? corporation.customer.name
									: '---';

								if (
									this.state.selectedCorporation &&
									this.state.selectedCorporation.id === corporation.id
								) {
									classes.push('selected');
								}

								return (
									<div
										key={corporation.id}
										className={classes.join(' ')}
										onClick={e => this.handleClickCorporation(e, corporation)}>
										<div className="corporation-card-details">
											<div>
												<h1>{corporation.name}</h1>
												<span className="corporation-card-label">
													Number
												</span>{' '}
												{corporation.number}
												<br />
												<span className="corporation-card-label">
													Customer
												</span>{' '}
												{customerName}
											</div>
											<Choose>
												<When
													condition={
														corporation.customerId ===
														this.props.customer.id
													}>
													<Tooltip text="Click to unlink this corporation">
														<a
															href="#"
															onClick={e =>
																this.handleClickUnlinkCorporation(
																	e,
																	corporation,
																)
															}>
															<Icon type="link" />
														</a>
													</Tooltip>
												</When>
												<When
													condition={
														this.state.filterType === 'all' &&
														corporation.customerId !==
															this.props.customer.id
													}>
													<Tooltip text="Click to link this corporation">
														<a
															href="#"
															onClick={e =>
																this.handleClickLinkCorporation(
																	e,
																	corporation,
																)
															}>
															<Icon type="unlink" />
														</a>
													</Tooltip>
												</When>
												<When
													condition={
														this.state.filterType === 'connected' &&
														corporation.customerId !==
															this.props.customer.id
													}>
													<Tooltip text="Click to view connected divisions">
														<Icon type="chevron-right" />
													</Tooltip>
												</When>
											</Choose>
										</div>
									</div>
								);
							})}
							<Pagination
								totalPages={Math.ceil(numTotalCorporations / PAGINATION_LIMIT)}
								currentPage={this.state.corporationsPage}
								onPageChange={page =>
									this.setState(
										{ corporationsPage: page },
										this.fetchCorporationsList,
									)
								}
							/>
						</When>
						<When condition={this.state.corporationsSearchValue}>
							<p>
								No{' '}
								<strong>
									{this.state.filterType === 'connected' ? 'connected' : ''}
								</strong>{' '}
								corporations matched &quote;
								<strong>{this.state.corporationsSearchValue}</strong>&quote;
							</p>
						</When>
						<Otherwise>
							<p>
								No{' '}
								<strong>
									{this.state.filterType === 'connected' ? 'connected' : ''}
								</strong>{' '}
								corporations were found
							</p>
						</Otherwise>
					</Choose>
				</div>
				<div className="cell right-column">
					<Choose>
						<When condition={this.state.selectedCorporation}>
							<SearchBar onEnter={value => this.handleSearchBuilders(value)} />

							<Choose>
								<When condition={isFetchingBuilders}>
									<p>
										<Spinner visible={isFetchingBuilders} />
									</p>
								</When>
								<When condition={builderResults.length}>
									{builderResults.map(builder => {
										const customerName = builder.customer
											? builder.customer.name
											: '---';

										return (
											<div key={builder.id} className="builder-card">
												<div className="builder-card-details">
													<div>
														<h1>{builder.name}</h1>
														<span className="builder-card-label">
															Number
														</span>{' '}
														{builder.number}
														<br />
														<span className="builder-card-label">
															Customer
														</span>{' '}
														{customerName}
													</div>
													<Choose>
														<When
															condition={
																this.state.selectedCorporation
																	.customerId ===
																this.props.customer.id
															}>
															<Tooltip
																position="left"
																text="You must unlink the corporation to link this division">
																<Icon type="link" />
															</Tooltip>
														</When>
														<When
															condition={
																builder.customerId ===
																this.props.customer.id
															}>
															<Tooltip
																position="left"
																text="Click to unlink this division">
																<a
																	href="#"
																	onClick={e =>
																		this.handleClickUnlinkBuilder(
																			e,
																			builder,
																		)
																	}>
																	<Icon type="link" />
																</a>
															</Tooltip>
														</When>
														<When
															condition={
																builder.customerId !==
																this.props.customer.id
															}>
															<Tooltip
																position="left"
																text="Click to link this division">
																<a
																	href="#"
																	onClick={e =>
																		this.handleClickLinkBuilder(
																			e,
																			builder,
																		)
																	}>
																	<Icon type="unlink" />
																</a>
															</Tooltip>
														</When>
													</Choose>
												</div>
											</div>
										);
									})}
									<Pagination
										totalPages={Math.ceil(numTotalBuilders / PAGINATION_LIMIT)}
										currentPage={this.state.buildersPage}
										onPageChange={page =>
											this.setState({ buildersPage: page }, () =>
												this.fetchBuildersList(),
											)
										}
									/>
								</When>
								<When condition={this.state.buildersSearchValue}>
									<p>
										No{' '}
										<strong>
											{this.state.filterType === 'connected'
												? 'connected'
												: ''}
										</strong>{' '}
										divisions matched &quote;
										<strong>{this.state.buildersSearchValue}</strong>&quote;
									</p>
								</When>
								<Otherwise>
									<p>
										No{' '}
										<strong>
											{this.state.filterType === 'connected'
												? 'connected'
												: ''}
										</strong>{' '}
										divisions were found for{' '}
										<strong>{this.state.selectedCorporation.name}</strong>
									</p>
								</Otherwise>
							</Choose>
						</When>
						<Otherwise>
							<p>
								<Icon type="chevron-left" /> Please choose a corporation from the
								list on your left
							</p>
						</Otherwise>
					</Choose>
				</div>
			</div>
		);
	}
}

LinkCustomerPane.propTypes = {
	customer: React.PropTypes.object,
};

const updateCorporationCustomerMutation = gql`
	mutation UpdateCorporationCustomerMutation($corporationId: EncodedID!, $customerId: EncodedID) {
		updateCorporation(input: { id: $corporationId, customerId: $customerId }) {
			id
		}
	}
`;

const updateBuilderCustomerMutation = gql`
	mutation UpdateBuilderCustomerMutation($builderId: EncodedID!, $customerId: EncodedID) {
		updateBuilder(input: { id: $builderId, customerId: $customerId }) {
			id
		}
	}
`;

export default compose(
	connect(state => ({
		user: state.auth.user,
	})),
	graphql(updateCorporationCustomerMutation, {
		props: ({ mutate }) => ({
			updateCorporation: (corporationId, customerId) =>
				mutate({
					variables: { corporationId, customerId },
				}),
		}),
	}),
	graphql(updateBuilderCustomerMutation, {
		props: ({ mutate }) => ({
			updateBuilder: (builderId, customerId) =>
				mutate({
					variables: { builderId, customerId },
				}),
		}),
	}),
	withApollo,
)(LinkCustomerPane);



// WEBPACK FOOTER //
// ./src/assets/js/containers/customers/LinkPane.js